import { i18n } from '@/common/locale';

/**
 * MCP tool 이름 → 라벨 i18n 키 + 아이콘.
 * 챗봇 답변의 도구 칩/상태표시(botMessage)와 환경설정의 도구 on/off 목록(ChatbotToolsTab)에서 공용 사용.
 */
export const TOOL_LABELS: Record<string, { labelKey: string; icon: string }> = {
  // Inventory / Health
  get_exemone_overview: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_EXEMONE_OVERVIEW',
    icon: 'ev-icon-info',
  },
  get_collection_status: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_COLLECTION_STATUS',
    icon: 'ev-icon-check-on',
  },
  get_system_health: { labelKey: 'WORD.CHATBOT.TOOL.GET_SYSTEM_HEALTH', icon: 'ev-icon-monitor' },
  list_targets_with_metrics: {
    labelKey: 'WORD.CHATBOT.TOOL.LIST_TARGETS_WITH_METRICS',
    icon: 'ev-icon-search',
  },
  get_target_info: { labelKey: 'WORD.CHATBOT.TOOL.GET_TARGET_INFO', icon: 'ev-icon-search' },
  get_target_relations: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_TARGET_RELATIONS',
    icon: 'ev-icon-search',
  },
  // Alarms
  get_active_alarms: { labelKey: 'WORD.CHATBOT.TOOL.GET_ACTIVE_ALARMS', icon: 'ev-icon-bell' },
  get_recent_alarms: { labelKey: 'WORD.CHATBOT.TOOL.GET_RECENT_ALARMS', icon: 'ev-icon-list' },
  // Metrics
  list_metric_catalog: {
    labelKey: 'WORD.CHATBOT.TOOL.LIST_METRIC_CATALOG',
    icon: 'ev-icon-search',
  },
  get_metric_trend: { labelKey: 'WORD.CHATBOT.TOOL.GET_METRIC_TREND', icon: 'ev-icon-graph' },
  get_metric_current: { labelKey: 'WORD.CHATBOT.TOOL.GET_METRIC_CURRENT', icon: 'ev-icon-monitor' },
  get_metric_table: { labelKey: 'WORD.CHATBOT.TOOL.GET_METRIC_TABLE', icon: 'ev-icon-graph' },
  // WAS
  get_was_overview: { labelKey: 'WORD.CHATBOT.TOOL.GET_WAS_OVERVIEW', icon: 'ev-icon-info' },
  get_was_memory_trend: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_WAS_MEMORY_TREND',
    icon: 'ev-icon-graph',
  },
  get_was_active_transactions: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_WAS_ACTIVE_TRANSACTIONS',
    icon: 'ev-icon-list',
  },
  get_was_top_transactions: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_WAS_TOP_TRANSACTIONS',
    icon: 'ev-icon-graph',
  },
  get_was_response_ranking: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_WAS_RESPONSE_RANKING',
    icon: 'ev-icon-graph',
  },
  get_slow_transaction_samples: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_SLOW_TRANSACTION_SAMPLES',
    icon: 'ev-icon-list',
  },
  get_was_remote_call_breakdown: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_WAS_REMOTE_CALL_BREAKDOWN',
    icon: 'ev-icon-search',
  },
  get_transaction_analysis: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_TRANSACTION_ANALYSIS',
    icon: 'ev-icon-graph',
  },
  get_cross_calls: { labelKey: 'WORD.CHATBOT.TOOL.GET_CROSS_CALLS', icon: 'ev-icon-search' },
  // Database
  get_db_overview: { labelKey: 'WORD.CHATBOT.TOOL.GET_DB_OVERVIEW', icon: 'ev-icon-info' },
  get_db_active_sessions: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_DB_ACTIVE_SESSIONS',
    icon: 'ev-icon-list',
  },
  get_db_lock_tree: { labelKey: 'WORD.CHATBOT.TOOL.GET_DB_LOCK_TREE', icon: 'ev-icon-graph' },
  get_db_parameters: { labelKey: 'WORD.CHATBOT.TOOL.GET_DB_PARAMETERS', icon: 'ev-icon-cog' },
  get_top_sql: { labelKey: 'WORD.CHATBOT.TOOL.GET_TOP_SQL', icon: 'ev-icon-list' },
  get_redis_health: { labelKey: 'WORD.CHATBOT.TOOL.GET_REDIS_HEALTH', icon: 'ev-icon-info' },
  get_host_system_info: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_HOST_SYSTEM_INFO',
    icon: 'ev-icon-info',
  },
  get_was_jvm_options: { labelKey: 'WORD.CHATBOT.TOOL.GET_WAS_JVM_OPTIONS', icon: 'ev-icon-cog' },
  recommend_alarm_thresholds: {
    labelKey: 'WORD.CHATBOT.TOOL.RECOMMEND_ALARM_THRESHOLDS',
    icon: 'ev-icon-bell',
  },
  // Host / K8s
  get_host_top_processes: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_HOST_TOP_PROCESSES',
    icon: 'ev-icon-list',
  },
  get_pod_resource_trend: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_POD_RESOURCE_TREND',
    icon: 'ev-icon-graph',
  },
  get_pod_outbound_calls: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_POD_OUTBOUND_CALLS',
    icon: 'ev-icon-search',
  },
  get_network_interface_stat: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_NETWORK_INTERFACE_STAT',
    icon: 'ev-icon-graph',
  },
  // Navigation / Action
  navigate_to_screen: {
    labelKey: 'WORD.CHATBOT.TOOL.NAVIGATE_TO_SCREEN',
    icon: 'ev-icon-allow2-right2',
  },
  find_dashboard: { labelKey: 'WORD.CHATBOT.TOOL.FIND_DASHBOARD', icon: 'ev-icon-search' },
  list_widget_packs: { labelKey: 'WORD.CHATBOT.TOOL.LIST_WIDGET_PACKS', icon: 'ev-icon-search' },
  compose_dashboard: {
    labelKey: 'WORD.CHATBOT.TOOL.COMPOSE_DASHBOARD',
    icon: 'ev-icon-allow2-right2',
  },
  get_related_targets_health: {
    labelKey: 'WORD.CHATBOT.TOOL.GET_RELATED_TARGETS_HEALTH',
    icon: 'ev-icon-graph',
  },
  open_target_detail: {
    labelKey: 'WORD.CHATBOT.TOOL.OPEN_TARGET_DETAIL',
    icon: 'ev-icon-allow2-right2',
  },
  open_alert_rule_detail: {
    labelKey: 'WORD.CHATBOT.TOOL.OPEN_ALERT_RULE_DETAIL',
    icon: 'ev-icon-allow2-right2',
  },
  open_aiops_slide: {
    labelKey: 'WORD.CHATBOT.TOOL.OPEN_AIOPS_SLIDE',
    icon: 'ev-icon-allow2-right2',
  },
  analyze_in_screen: {
    labelKey: 'WORD.CHATBOT.TOOL.ANALYZE_IN_SCREEN',
    icon: 'ev-icon-allow2-right2',
  },
  generate_report: { labelKey: 'WORD.CHATBOT.TOOL.GENERATE_REPORT', icon: 'ev-icon-document' },
  // Specialist
  summon_specialist: {
    labelKey: 'WORD.CHATBOT.TOOL.SUMMON_SPECIALIST',
    icon: 'ev-icon-allow2-right2',
  },
};

/** 도구의 짧은 역할 라벨(i18n 적용). 매핑이 없으면 빈 문자열. */
export const toolRole = (name: string): string => {
  const key = TOOL_LABELS[name]?.labelKey;
  return key ? i18n.global.t(key) : '';
};
